/**
 * The authenticated frame for both apps, and the one piece of markup in the
 * design system that a keyboard or a screen reader meets on every single
 * screen. That is why its sidebar is shaped the way it is.
 *
 * **A nav group is a heading and a list, not two styled `div`s.** The eyebrow
 * above "Reach" was a `div` with small-caps classes and the items were siblings
 * in a flex column: it looked like a grouped navigation and was, to anything
 * that is not a pair of eyes, nine unrelated links in a row. It is now an `h2`
 * — so heading navigation reaches it and the group can be skipped rather than
 * heard out — over a `ul` that names it with `aria-labelledby`, so an item is
 * announced as one of four inside Reach rather than one of nine inside the
 * sidebar. The styling is unchanged; Tailwind's preflight strips a heading's
 * default weight and a list's bullets, so this is semantics bought for nothing.
 * Slice 15 grouped the sidebar by product pillar and made the count of groups
 * grow rather than shrink, which is what turned this from tidiness into the
 * difference between navigable and not. These `h2`s precede the top bar's `h1`
 * in the DOM, which is what a sidebar before a main column means and is not a
 * level skipped: they sit inside the `nav` landmark and are heard as part of it.
 *
 * **A section either has destinations or it has a note — never both.** Slice 15
 * needs Flow and Sense in the sidebar as pillars that do not exist yet, which
 * is a named group with one short line in it and nothing to click. The rejected
 * alternative was an `AppShellNavItem` with `disabled: true`: a disabled
 * control is still a control, it invites the click it then refuses, and half
 * the implementations leave it focusable so a keyboard user tabs into a thing
 * that does nothing. {@link AppShellNavSection} is a union instead, and the
 * `items?: never` / `note?: never` pairing is load-bearing — a group cannot
 * carry a link *and* say the software is not built, so no future edit can quietly
 * put a destination inside "Not built yet".
 *
 * **A group may carry an icon, beside its heading and never inside it.** Slice
 * 65 gave each pillar a glyph (a megaphone for Reach, two speech bubbles for
 * Connect) so the groups can be told apart at a glance. The icon is an
 * `aria-hidden` sibling of the `h2` in a flex row, not a child: inside the
 * heading it would join the heading's accessible name and its text content,
 * and both the `aria-labelledby` on the list and verify-nav-shell — which reads
 * each heading's text exactly — would hear a glyph that says nothing. Optional
 * on both arms, and when it is absent the heading renders exactly as it did
 * before, wrapper and all, which is what the admin app gets: it passes none.
 *
 * **Two kinds of group, drawn differently — the owner's redesign, 17 Oct 2026.**
 * With six groups the sidebar read as one crowded column: every heading was
 * the same 0.62rem grey mono caps, the pillar glyph sat beside a heading that
 * barely registered and then again on the item below it (a megaphone over a
 * megaphone), and nothing marked where one group ended. Now:
 *
 *   - A group **with an icon** is a product area (Reach, Connect, Sense, the
 *     shared layers). Its icon sits in a small tinted tile, its name is set in
 *     readable text, and its destinations hang beneath it on a guide line, as
 *     a list *inside* that area — without icons of their own, because the
 *     tile already says what the group is and a second glyph per row was the
 *     clutter.
 *   - A group **without an icon** is machinery (Workspace; the admin app's one
 *     group). It keeps the quiet mono caps heading and its items keep their
 *     icons, which is how it looked before and how the admin app still looks.
 *
 * **A group may be pinned to the foot of the sidebar** (`pinned`). The tenant
 * app pins Workspace, so the scrolling area holds the product and the foot holds
 * the workspace's own screens, just above the signed-in user — the line between
 * "where you work" and "the machinery" is a rule drawn across the sidebar rather
 * than a heading lost among five others. Pinned groups render after the rest in
 * the DOM, which is the order they always had, so the reading order a screen
 * reader hears is unchanged.
 *
 * The rest of the frame — the sticky top bar, the content column and the drawer
 * this sidebar becomes below `lg` — is documented on {@link AppShell} itself.
 */
import { Menu, X } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { cn } from '../lib/cn';
import { focusRing } from '../lib/focus-ring';
import { WendMark } from './WendMark';

/* -------------------------------------------------------------------------- */
/* nav                                                                         */
/* -------------------------------------------------------------------------- */

export interface AppShellNavItemRenderProps {
  className: string;
  children: ReactNode;
  'aria-current': 'page' | undefined;
  onClick: () => void;
}

export interface AppShellNavItem {
  key: string;
  label: string;
  icon?: ReactNode;
  /** Rendered as an `<a href>` unless `render` is supplied. */
  href?: string;
  active?: boolean;
  /** Used when there is no `href` — renders a `<button>`. */
  onSelect?: () => void;
  /**
   * Escape hatch so the shell stays router-agnostic. Apps using a client
   * router pass their own link component:
   *
   *     render: (props) => <NavLink to="/tenants" {...props} />
   */
  render?: (props: AppShellNavItemRenderProps) => ReactNode;
}

/**
 * A group of destinations.
 *
 * `label` stays optional because the first group — the one destination that
 * sits above the pillars — has no heading and needs none: a heading over a
 * single "Overview" link says the word twice.
 */
export interface AppShellNavLinkSection {
  key: string;
  /** Rendered as an `h2` above the list, and names the list to a screen reader. */
  label?: string;
  /**
   * Decorative glyph before the heading — see the file header for why it sits
   * beside the `h2` rather than in it. Ignored when there is no `label`: an
   * icon over an unheaded group would be a heading drawn without the words.
   */
  icon?: ReactNode;
  /** Drawn at the foot of the sidebar, below a rule, instead of in the scrolling list. */
  pinned?: boolean;
  items: AppShellNavItem[];
  note?: never;
}

/**
 * A named group with nothing in it, and one short line saying why.
 *
 * `label` is required here where it is optional above: an unattached sentence
 * floating in the sidebar is not a group, and the whole point of this arm is to
 * name a pillar and then say what is true of it. Keep the line short — four
 * words, not a paragraph. It is read by everyone who opens the app and it is
 * not the place to explain a roadmap.
 */
export interface AppShellNavNoteSection {
  key: string;
  label: string;
  /** Decorative glyph before the heading, as on {@link AppShellNavLinkSection}. */
  icon?: ReactNode;
  /** As on {@link AppShellNavLinkSection}. */
  pinned?: boolean;
  note: string;
  items?: never;
}

export type AppShellNavSection = AppShellNavLinkSection | AppShellNavNoteSection;

function NavItem({
  item,
  onNavigate,
  bare = false,
}: {
  item: AppShellNavItem;
  onNavigate: () => void;
  /** Inside a group with an icon: no glyph of its own (see the file header). */
  bare?: boolean;
}): ReactElement {
  const className = cn(
    'flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-[7px] text-left',
    'text-[0.84rem] leading-5 text-muted no-underline transition-colors duration-150 ease-wend',
    "[&_svg:not([class*='size-'])]:size-4",
    'hover:bg-white/[0.06] hover:text-text',
    focusRing,
    item.active && 'bg-brand-a/20 text-white shadow-[inset_0_0_0_1px_rgba(155,94,246,0.35)]',
  );

  const content = (
    <>
      {!bare && item.icon && <span className="grid size-4 shrink-0 place-items-center">{item.icon}</span>}
      <span className="truncate">{item.label}</span>
    </>
  );

  const ariaCurrent = item.active ? ('page' as const) : undefined;

  const handleClick = () => {
    item.onSelect?.();
    onNavigate();
  };

  if (item.render) {
    return (
      <>
        {item.render({ className, children: content, 'aria-current': ariaCurrent, onClick: handleClick })}
      </>
    );
  }

  if (item.href !== undefined) {
    return (
      <a href={item.href} className={className} aria-current={ariaCurrent} onClick={handleClick}>
        {content}
      </a>
    );
  }

  return (
    <button type="button" className={className} aria-current={ariaCurrent} onClick={handleClick}>
      {content}
    </button>
  );
}

/**
 * One group: a heading (or none) and its list or its note.
 *
 * A group with an icon is drawn as a product area — tile, readable name, its
 * destinations indented on a guide line that starts under the tile's centre
 * (10px in, half of 22px: 21px). A group without one keeps the quiet mono
 * heading. See the file header for why the two differ.
 */
function NavSection({
  section,
  sidebarId,
  onNavigate,
}: {
  section: AppShellNavSection;
  sidebarId: string;
  onNavigate: () => void;
}): ReactElement {
  // Unique per shell instance, so two shells on one page could not produce two
  // `#nav-reach` and send `aria-labelledby` to whichever the parser saw first.
  const headingId = `${sidebarId}-${section.key}`;
  const area = section.label !== undefined && section.icon !== undefined;

  let heading: ReactNode = null;
  if (section.label !== undefined) {
    heading = area ? (
      // The `h2` holds the word and nothing else; the tile is its sibling.
      <div className="flex items-center gap-2.5 px-2.5 pb-1 pt-1">
        <span
          aria-hidden="true"
          className="grid size-[22px] shrink-0 place-items-center rounded-md bg-brand-a/15 text-brand-b ring-1 ring-inset ring-brand-b/25 [&_svg:not([class*='size-'])]:size-3.5"
        >
          {section.icon}
        </span>
        <h2 id={headingId} className="text-[0.8rem] font-semibold tracking-[-0.005em] text-text">
          {section.label}
        </h2>
      </div>
    ) : (
      <h2
        id={headingId}
        className="px-2.5 pb-1 pt-2 font-mono text-[0.62rem] font-normal uppercase tracking-[0.14em] text-muted"
      >
        {section.label}
      </h2>
    );
  }

  return (
    <div className={cn(section.label === undefined ? 'mb-2' : 'mt-3 first:mt-0')}>
      {heading}
      {section.items === undefined ? (
        <p
          className={cn(
            'text-[0.78rem] text-muted',
            area ? 'ml-[21px] border-l border-line py-1 pl-3' : 'px-2.5 pb-1',
          )}
        >
          {section.note}
        </p>
      ) : (
        <ul
          aria-labelledby={section.label === undefined ? undefined : headingId}
          className={cn('flex flex-col gap-0.5', area && 'ml-[21px] border-l border-line pl-2')}
        >
          {section.items.map((item) => (
            <li key={item.key}>
              <NavItem item={item} onNavigate={onNavigate} bare={area} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* shell                                                                       */
/* -------------------------------------------------------------------------- */

export interface AppShellProps {
  children: ReactNode;
  /** Sidebar navigation, grouped. Omit for a chrome-only shell. */
  nav?: AppShellNavSection[];
  /** Replaces the default Wend mark + wordmark in the sidebar head. */
  brand?: ReactNode;
  /** Page title in the top bar. */
  title?: ReactNode;
  /** Actions aligned to the right of the top bar. */
  actions?: ReactNode;
  /** Pinned to the bottom of the sidebar — the user block and sign out. */
  sidebarFooter?: ReactNode;
  className?: string;
}

/**
 * The authenticated frame for both apps: sidebar, sticky top bar, content
 * column. Below `lg` the sidebar becomes a drawer that the top bar toggles;
 * it closes on Escape, on backdrop click and on navigation, returning focus to
 * the toggle.
 */
export function AppShell({
  children,
  nav = [],
  brand,
  title,
  actions,
  sidebarFooter,
  className,
}: AppShellProps) {
  const [open, setOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const sidebarId = `wend-sidebar-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const closeAndRestoreFocus = useCallback(() => {
    setOpen(false);
    toggleRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className={cn('relative min-h-dvh bg-ink text-text', className)}>
      <div className="wend-ambient" aria-hidden="true" />

      <div className="relative z-10 flex min-h-dvh">
        {open && (
          <button
            type="button"
            aria-label="Close navigation"
            onClick={closeAndRestoreFocus}
            className="fixed inset-0 z-30 cursor-default bg-ink/70 backdrop-blur-sm lg:hidden"
          />
        )}

        <aside
          id={sidebarId}
          className={cn(
            'fixed inset-y-0 left-0 z-40 flex w-[248px] flex-col border-r border-line',
            'bg-surface-1/95 backdrop-blur-xl transition-[transform,visibility] duration-200 ease-wend',
            'lg:sticky lg:inset-auto lg:top-0 lg:z-auto lg:h-dvh lg:visible lg:translate-x-0 lg:bg-black/20',
            // `invisible` keeps the closed drawer out of the tab order and the
            // accessibility tree; transitioning it holds it visible for the
            // duration of the slide-out.
            open ? 'visible translate-x-0' : 'invisible -translate-x-full',
          )}
        >
          <div className="flex h-14 shrink-0 items-center gap-2.5 px-4">
            {brand ?? (
              <>
                <WendMark />
                <span className="text-[1.05rem] font-semibold tracking-[-0.02em]">wend</span>
              </>
            )}
            <button
              type="button"
              onClick={closeAndRestoreFocus}
              aria-label="Close navigation"
              className={cn(
                'ml-auto cursor-pointer rounded-sm p-1.5 text-muted transition-colors hover:text-text lg:hidden',
                focusRing,
              )}
            >
              <X className="size-4" aria-hidden="true" />
            </button>
          </div>

          {nav.length > 0 && (
            <nav aria-label="Main" className="flex min-h-0 flex-1 flex-col">
              <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-4 pt-1">
                {nav
                  .filter((section) => section.pinned !== true)
                  .map((section) => (
                    <NavSection key={section.key} section={section} sidebarId={sidebarId} onNavigate={close} />
                  ))}
              </div>
              {nav.some((section) => section.pinned === true) && (
                <div className="shrink-0 border-t border-line px-3 pb-2 pt-1">
                  {nav
                    .filter((section) => section.pinned === true)
                    .map((section) => (
                      <NavSection key={section.key} section={section} sidebarId={sidebarId} onNavigate={close} />
                    ))}
                </div>
              )}
            </nav>
          )}

          {nav.length === 0 && <div className="min-h-0 flex-1" />}

          {sidebarFooter && (
            <div className="shrink-0 border-t border-line p-3">{sidebarFooter}</div>
          )}
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-3 border-b border-line bg-ink/75 px-pad backdrop-blur-xl">
            <button
              ref={toggleRef}
              type="button"
              onClick={() => setOpen((value) => !value)}
              aria-label="Toggle navigation"
              aria-expanded={open}
              aria-controls={sidebarId}
              className={cn(
                'cursor-pointer rounded-sm p-1.5 text-muted transition-colors hover:text-text lg:hidden',
                focusRing,
              )}
            >
              <Menu className="size-5" aria-hidden="true" />
            </button>

            {title !== undefined && (
              <h1 className="min-w-0 truncate text-[1.02rem] font-semibold tracking-[-0.022em]">
                {title}
              </h1>
            )}

            {actions && <div className="ml-auto flex shrink-0 items-center gap-2">{actions}</div>}
          </header>

          <main className="mx-auto w-full max-w-page flex-1 px-pad py-8">{children}</main>
        </div>
      </div>
    </div>
  );
}
